import { Component } from "react";
import {
  extract,
  CartContextConsumer,
  EventAggregator,
  GhostButton,
  OutlineButton,
  LanguageSelector,
} from "@parksandresorts/core";
import { Link } from "gatsby";
import styles from "./main-navigation.module.less";
import { graphql } from "gatsby";
import Cart from "components/block-elements/cart/cart.js";
import { GlobalContext } from "components/stores/global-context";
import EventAttributeStore from "components/stores/event-attribute-store";
import LunrSearch from "components/block-elements/search/lunr-search.tsx";

import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import LoginLink from "components/block-elements/login-link/login-link";
import { GatsbyImage } from "gatsby-plugin-image";
import { motion, AnimatePresence } from "framer-motion";

const topBannerHeight = 56;
const yPosStartFade = 300;
const yPosEndFade = 350;
const relevantYPositions = [0, topBannerHeight, yPosStartFade, yPosEndFade];

function calculateSetFixed(yPos) {
  return yPos > topBannerHeight;
}

function calculateNavigationFadeInPercentage(yPos, prefillNavigation) {
  if (prefillNavigation) {
    return 100;
  }

  const percentageCompleted = ((yPos - yPosStartFade) / (yPosEndFade - yPosStartFade)) * 100;
  return Math.max(0, Math.min(percentageCompleted, 100));
}

export default class MainNavigation extends Component {
  constructor(props) {
    super(props);

    this.menuIconTitle = extract(props, "navigation.menuIconTitle", "");
    this.logoTransparentUrl = extract(this.props, "navigation.logoTransparent.internalLink.slug");
    this.logoTransparentImageUrl = extract(this.props, "navigation.logoTransparent.image.file.url");
    this.logoFilledUrl = extract(this.props, "navigation.logoFilled.internalLink.slug");
    this.logoFilledImageUrl = extract(this.props, "navigation.logoFilled.image.file.url");
    this.searchNoResultText = extract(this.props, "navigation.searchNoResultText", "");
    this.searchPlaceholderLabel = extract(this.props, "navigation.searchPlaceholderLabel", "");
    this.searchResultHeader = extract(this.props, "navigation.searchResultHeader", "");
    this.searchResultBackground = extract(this.props, "navigation.searchResultBackground");
    this.linkGroups = extract(props, "navigation.linkGroups");
    this.extraWideLogo = extract(this.props, "navigation.extraWideLogo");
    this.onOpenMenuSubscription = null;

    this.languageSelectionList = extract(props, "navigation.languageSelectionList", []);

    this.onTransitionEndCollapsedElement = this.onTransitionEndCollapsedElement.bind(this);

    this.state = {
      yPos: 0,
    };

    this.loginEnabled = extract(props, "siteConfig.enableLogin", false);
  }

  componentDidMount() {
    this._isMounted = true;
    this.onOpenMenuSubscription = EventAggregator.subscribe("onToggleMenu", this.openMenuEvent);

    setTimeout(() => {
      this.onOrientationChangeSubscription = EventAggregator.subscribe("onOrientationChange", (e) =>
        this.onOrientationChange(e),
      );
      this.onScrollSubscription = EventAggregator.subscribe("onScroll", (yPos) => {
        if (this._isMounted) {
          this.onScroll(yPos);
        }
      });
    }, 25);
  }

  componentWillUnmount() {
    if (this.onOrientationChangeSubscription) {
      this.onOrientationChangeSubscription.remove();
    }
    if (this.onScrollSubscription) {
      this.onScrollSubscription.remove();
    }
    if (this.onOpenMenuSubscription) {
      this.onOpenMenuSubscription.remove();
    }
    this._isMounted = false;
  }

  openMenuEvent = () => {
    this.toggleMore();
  };

  onOrientationChange() {
    setTimeout(() => {
      if (this._isMounted) {
        this.onScroll();
      }
    }, 500);
  }

  onScroll(yPos) {
    const minRelevantYPos = Math.min(...relevantYPositions);
    const maxRelevantYPos = Math.max(...relevantYPositions);

    // optimization to avoid unnecessary re-renders
    yPos = Math.max(minRelevantYPos, Math.min(yPos, maxRelevantYPos));
    if (yPos !== this.state.yPos) {
      this.setState({
        yPos,
      });
    }
  }

  toggleCart() {
    const showCart = !this.context.showCart;
    this.context.setValue(
      {
        showCart: showCart,
        showMenu: false,
        showSearch: false,
        isAnimating: true,
        isAnimationDone: false,
      },
      () => {
        if (!showCart) {
          this.context.setValue({ isMainVisible: true });
        }
      },
    );
  }

  toggleMore() {
    const showMenu = !this.context.showMenu;
    this.context.setValue(
      {
        showMenu: showMenu,
        showCart: false,
        showSearch: false,
        isAnimating: true,
        isAnimationDone: false,
      },
      () => {
        if (!showMenu) {
          this.context.setValue({ isMainVisible: true });
        }
      },
    );
  }

  closeMenu() {
    this.context.setValue({
      showMenu: false,
      showCart: false,
      showSearch: false,
      isMainVisible: true,
    });
  }

  toggleSearch() {
    const showSearch = !this.context.showSearch;
    this.context.setValue(
      {
        showSearch: showSearch,
        showCart: false,
        isAnimating: true,
        isAnimationDone: false,
      },
      () => {
        if (!showSearch) {
          this.context.setValue({ isMainVisible: true });
        }
      },
    );
  }

  setTotalItemsInCart(cartContext) {
    if (typeof window !== "undefined" && (cartContext.isInitialCartFetch === null || cartContext.isInitialCartFetch)) {
      const cartState = cartContext;

      if (cartState && cartState.totalItems) {
        return cartState.totalItems;
      }
    }

    return cartContext.totalItems ? cartContext.totalItems : 0;
  }

  onTransitionEndCollapsedElement(event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    this.setAnimationDone();

    if (!this.context.isMainVisible) {
      return;
    }

    if (
      this.context.showMenu ||
      this.context.showSearch ||
      (this.context.showCart && EventAttributeStore.getCurrentWindowWidth() < 767)
    ) {
      this.context.setValue({ isMainVisible: false });

      // Required to allow for scroll inside the open menu.
      event.target.style.position = "relative";
    }
  }

  setAnimationDone() {
    this.context.setValue({
      isAnimating: false,
      isAnimationDone: true,
    });
  }

  onClickLogo() {
    this.context.setValue({ isMainVisible: true, showMenu: false, showSearch: false });
  }

  render() {
    const navigationFadeInPercentage = calculateNavigationFadeInPercentage(
      this.state.yPos,
      this.props.prefillNavigation,
    );
    const isFixed = !this.props.isShowingTopBanner || calculateSetFixed(this.state.yPos);

    const opacityLayerStyle = {
      opacity: navigationFadeInPercentage / 100,
    };

    const transparentLogoStyle = {
      opacity: (100 - navigationFadeInPercentage) / 100,
    };

    return (
      <nav className={isFixed ? "" : styles.mainNavigationAbsolute}>
        <div
          className={[
            styles.mainNavigation,
            navigationFadeInPercentage > 50 ? styles.mainNavigation_filled : "",
            isFixed ? "" : styles.mainNavigationAbsolute,
          ].join(" ")}
        >
          <div style={opacityLayerStyle} className={styles.opacityLayer} />
          <div className={[styles.innerWrapper].join(" ")}>
            <div className={styles.language}>
              <LanguageSelector locales={this.languageSelectionList?.languages ?? []} />
            </div>
            <div className={styles.search}>
              <GhostButton
                forwardRef={(ref) => {
                  this.ref_searchIconBtn = ref;
                }}
                onClick={() => this.toggleSearch()}
                label="Sök"
                labelClass={styles.btnLabel}
                iconLeft="icon-search"
                className={[styles.menuBtn, styles.searchBtn, styles.btnIconLeft, "waves-effect"].join(" ")}
              />
            </div>
            <LinkWrapper url={this.logoTransparentUrl} className={styles.logoWrapper}>
              <div
                style={transparentLogoStyle}
                className={[styles.logo, this.extraWideLogo ? styles.extraWideLogo : ""].join(" ")}
              >
                {this.logoTransparentImageUrl ? (
                  <img className={styles.logoImage} src={this.logoTransparentImageUrl} alt="Gröna Lund" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 79 16">
                    <g fill="#fff" fillRule="evenodd" transform="translate(.08 .138)">
                      <path d="M9.66206971 6.97710345L9.67917316 7.59641379C9.67806971 11.1942069 9.06096626 15.6626207 5.02510419 15.6640005 1.22896626 15.6656552-.00110270667 11.894069-3.55271368e-15 8.19944828.00110418989 4.58206897 1.21682833.577931034 4.99586281.576275517 7.08193178.575448276 8.63779385 2.06427586 9.38951798 4.17241379L6.02041453 5.58593103C5.98620764 5.0057931 5.64441453 4.09655172 5.02896626 4.09710345 3.42151798 4.09765517 3.55724212 7.44386207 3.55696626 8.54648276 3.60800074 9.59117241 3.5384835 12.1442759 4.88965591 12.1437241 5.81296626 12.1437241 6.10400074 10.9053793 6.08717316 10.054069L4.8046904 10.0546207 4.80551798 6.97903448 9.66206971 6.97710345M15.991449 9.21710345C15.6835869 9.04303448 15.2904835 8.84965517 15.0510352 8.84993103 13.8027594 8.85048276 13.6482766 10.2044138 13.6480007 11.2684138L13.6466214 15.408 10.2948973 15.4088276 10.2982076 5.19558621 13.6670352 5.19448276 13.4783456 7.14813793 13.5125525 7.18703448C13.8893801 5.92937931 14.7445525 4.92331034 15.9928283 4.98068966L15.991449 9.21710345M21.0240007 2.21186207C21.0245525 1.37986207 21.6573801.664 22.4099318.663723801 23.1793111.663172414 23.8457938 1.33986207 23.8457938 2.24937931 23.845518 3.11972414 23.2297938 3.81627586 22.4430352 3.81682767 21.6562766 3.81710345 21.0237249 3.10124138 21.0240007 2.21186207zM17.2962765 2.21296552C17.2965525 1.38151724 17.9293801.66537931 18.6819318.665103364 19.4513111.664827586 20.1180697 1.34151724 20.1177939 2.25048276 20.117518 3.12110345 19.5017938 3.81793103 18.7150352 3.81820698 17.9285525 3.81848276 17.2960007 3.10289655 17.2962765 2.21296552zM19.6364141 10.297931C19.6361387 11.3423448 19.7553111 12.7737931 20.5588973 12.7735172 21.3627594 12.7732414 21.4830352 11.3415172 21.4833116 10.2971034 21.4835869 9.25268966 21.3644145 7.82151724 20.5605525 7.8217931 19.7569663 7.8217931 19.6366904 9.25324138 19.6364141 10.297931zM24.7666218 10.296C24.765518 13.1588966 23.4480007 15.6157241 20.5580697 15.6168282 17.6681387 15.6182069 16.3520007 13.161931 16.3531035 10.2990345 16.3539318 7.43613793 17.671449 4.97903448 20.5616559 4.97793066 23.4515869 4.97682759 24.767449 7.43310345 24.7666218 10.296L24.7666218 10.296zM28.6513111 7.08193103L28.685518 7.12082759C29.0110352 6.01793103 29.8833111 4.97296552 30.9950352 4.97241367 32.9271732 4.97186207 33.405518 6.82868966 33.4046915 8.66648276L33.4027594 15.398069 30.0510352 15.3991724 30.0526904 10.2344828C30.0526904 9.36413793 30.0019318 8.74510345 29.4377938 8.74510345 28.7878628 8.74565517 28.7362766 9.4422069 28.7357249 10.4866207L28.7343456 15.3997241 25.3826214 15.4008276 25.3859318 5.18758621 28.7376559 5.1862069 28.6513111 7.08193103M38.8813801 11.5451034C38.9840007 11.0226207 38.9842766 10.4424828 38.9842766 10.1522759 38.9842766 9.88137931 39.0019318 9.37848276 38.9164145 8.9142069 38.8311732 8.46951724 38.6604145 8.08248276 38.2670352 8.08275847 37.8565525 8.08275847 37.6684145 8.54731034 37.565518 9.10841379 37.4797249 9.64993103 37.4797249 10.2882759 37.479449 10.6168276 37.479449 10.9073103 37.4962766 11.3713103 37.581518 11.7776552 37.6673111 12.1644138 37.8380697 12.4742069 38.1627594 12.4739312 38.5903456 12.4736552 38.7958628 12.048 38.8813801 11.5451034zM42.3346214 15.3931034L38.9828973 15.3944828 39.1371042 13.7111724 39.1031732 13.6725517C38.6753111 14.7561379 37.8882766 15.6074483 36.7939318 15.6080002 34.4681387 15.6088276 34.0245525 12.1853793 34.0253789 10.1928276 34.0259318 8.18096552 34.5572421 4.96993103 36.8659318 4.96910317 37.9431732 4.96855172 38.6096559 5.78068966 39.0369663 6.78648276L39.0711732 6.74786207 38.9348973 5.18096552 42.3379318 5.17958621 42.3346214 15.3931034 42.3346214 15.3931034z" />
                      <polyline points="46.709 15.433 46.714 .848 50.202 .847 50.199 11.988 52.815 11.988 52.814 15.431 46.709 15.433" />
                      <path d="M56.5842766 5.21462069L56.5826214 10.7084138C56.5823456 11.9271724 56.6504835 12.4686897 57.2320007 12.4684138 57.8303456 12.4681379 57.8990352 11.945931 57.8995869 10.7078621L57.901518 5.21434483 61.2529663 5.21296552 61.2507594 11.2289655C61.2502076 14.2466207 59.693518 15.64 57.2311732 15.6408281 54.6317249 15.641931 53.2300697 14.0173793 53.2308969 11.232L53.2328283 5.216 56.5842766 5.21462069M65.1713111 7.10565517L65.205518 7.14427586C65.5310352 6.04165517 66.4033111 4.99668966 67.5147594 4.99641352 69.4471732 4.99558621 69.925518 6.85268966 69.9246915 8.6902069L69.9227594 15.4215172 66.5707594 15.4228966 66.5724145 10.2584828C66.5726904 9.38758621 66.5216559 8.76882759 65.957518 8.76882759 65.3073111 8.76910345 65.2562766 9.46565517 65.2557249 10.5103448L65.2540697 15.4234483 61.9026214 15.4248276 61.9056559 5.21131034 65.2576559 5.2102069 65.1713111 7.10565517M75.3310357 10.2342069C75.3313111 9.22813793 75.2121387 8.14455172 74.5966904 8.14482753 73.895449 8.14537931 73.7241387 9.13213793 73.7238613 10.2537931 73.7233111 11.2984828 73.8769663 12.3815172 74.5266214 12.3815172 75.2620697 12.3812414 75.3307594 11.3169655 75.3310357 10.2342069zM75.2270352 14.0835862L75.1931042 14.0446897C74.7481387 14.9732414 74.1837249 15.6311724 73.1404145 15.6317241 70.7806904 15.6322759 70.2686904 12.4601379 70.269517 10.409931 70.2703456 8.30151724 70.7158628 4.99365517 73.1440007 4.99282759 74.1873111 4.99227586 74.8537938 5.64965517 75.2468973 6.67475862L75.2811042 6.63613793C75.2642766 6.17186207 75.2300697 5.70731034 75.2300697 5.24358621L75.2320007.00110344828 78.5837249-1.77635684e-14 78.5784835 15.4171034 75.1583456 15.4182069 75.2270352 14.0835862 75.2270352 14.0835862z" />
                    </g>
                  </svg>
                )}
              </div>
              <div
                style={opacityLayerStyle}
                className={[styles.logo, styles.logo__filled, this.extraWideLogo ? styles.extraWideLogo : ""].join(" ")}
              >
                {this.logoFilledImageUrl ? (
                  <img className={styles.logoImage} src={this.logoFilledImageUrl} alt="Gröna Lund" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 79 16">
                    <g fill="#006649" fillRule="evenodd" transform="translate(.08 .138)">
                      <path d="M9.66206971 6.97710345L9.67917316 7.59641379C9.67806971 11.1942069 9.06096626 15.6626207 5.02510419 15.6640005 1.22896626 15.6656552-.00110270667 11.894069-3.55271368e-15 8.19944828.00110418989 4.58206897 1.21682833.577931034 4.99586281.576275517 7.08193178.575448276 8.63779385 2.06427586 9.38951798 4.17241379L6.02041453 5.58593103C5.98620764 5.0057931 5.64441453 4.09655172 5.02896626 4.09710345 3.42151798 4.09765517 3.55724212 7.44386207 3.55696626 8.54648276 3.60800074 9.59117241 3.5384835 12.1442759 4.88965591 12.1437241 5.81296626 12.1437241 6.10400074 10.9053793 6.08717316 10.054069L4.8046904 10.0546207 4.80551798 6.97903448 9.66206971 6.97710345M15.991449 9.21710345C15.6835869 9.04303448 15.2904835 8.84965517 15.0510352 8.84993103 13.8027594 8.85048276 13.6482766 10.2044138 13.6480007 11.2684138L13.6466214 15.408 10.2948973 15.4088276 10.2982076 5.19558621 13.6670352 5.19448276 13.4783456 7.14813793 13.5125525 7.18703448C13.8893801 5.92937931 14.7445525 4.92331034 15.9928283 4.98068966L15.991449 9.21710345M21.0240007 2.21186207C21.0245525 1.37986207 21.6573801.664 22.4099318.663723801 23.1793111.663172414 23.8457938 1.33986207 23.8457938 2.24937931 23.845518 3.11972414 23.2297938 3.81627586 22.4430352 3.81682767 21.6562766 3.81710345 21.0237249 3.10124138 21.0240007 2.21186207zM17.2962765 2.21296552C17.2965525 1.38151724 17.9293801.66537931 18.6819318.665103364 19.4513111.664827586 20.1180697 1.34151724 20.1177939 2.25048276 20.117518 3.12110345 19.5017938 3.81793103 18.7150352 3.81820698 17.9285525 3.81848276 17.2960007 3.10289655 17.2962765 2.21296552zM19.6364141 10.297931C19.6361387 11.3423448 19.7553111 12.7737931 20.5588973 12.7735172 21.3627594 12.7732414 21.4830352 11.3415172 21.4833116 10.2971034 21.4835869 9.25268966 21.3644145 7.82151724 20.5605525 7.8217931 19.7569663 7.8217931 19.6366904 9.25324138 19.6364141 10.297931zM24.7666218 10.296C24.765518 13.1588966 23.4480007 15.6157241 20.5580697 15.6168282 17.6681387 15.6182069 16.3520007 13.161931 16.3531035 10.2990345 16.3539318 7.43613793 17.671449 4.97903448 20.5616559 4.97793066 23.4515869 4.97682759 24.767449 7.43310345 24.7666218 10.296L24.7666218 10.296zM28.6513111 7.08193103L28.685518 7.12082759C29.0110352 6.01793103 29.8833111 4.97296552 30.9950352 4.97241367 32.9271732 4.97186207 33.405518 6.82868966 33.4046915 8.66648276L33.4027594 15.398069 30.0510352 15.3991724 30.0526904 10.2344828C30.0526904 9.36413793 30.0019318 8.74510345 29.4377938 8.74510345 28.7878628 8.74565517 28.7362766 9.4422069 28.7357249 10.4866207L28.7343456 15.3997241 25.3826214 15.4008276 25.3859318 5.18758621 28.7376559 5.1862069 28.6513111 7.08193103M38.8813801 11.5451034C38.9840007 11.0226207 38.9842766 10.4424828 38.9842766 10.1522759 38.9842766 9.88137931 39.0019318 9.37848276 38.9164145 8.9142069 38.8311732 8.46951724 38.6604145 8.08248276 38.2670352 8.08275847 37.8565525 8.08275847 37.6684145 8.54731034 37.565518 9.10841379 37.4797249 9.64993103 37.4797249 10.2882759 37.479449 10.6168276 37.479449 10.9073103 37.4962766 11.3713103 37.581518 11.7776552 37.6673111 12.1644138 37.8380697 12.4742069 38.1627594 12.4739312 38.5903456 12.4736552 38.7958628 12.048 38.8813801 11.5451034zM42.3346214 15.3931034L38.9828973 15.3944828 39.1371042 13.7111724 39.1031732 13.6725517C38.6753111 14.7561379 37.8882766 15.6074483 36.7939318 15.6080002 34.4681387 15.6088276 34.0245525 12.1853793 34.0253789 10.1928276 34.0259318 8.18096552 34.5572421 4.96993103 36.8659318 4.96910317 37.9431732 4.96855172 38.6096559 5.78068966 39.0369663 6.78648276L39.0711732 6.74786207 38.9348973 5.18096552 42.3379318 5.17958621 42.3346214 15.3931034 42.3346214 15.3931034z" />
                      <polyline points="46.709 15.433 46.714 .848 50.202 .847 50.199 11.988 52.815 11.988 52.814 15.431 46.709 15.433" />
                      <path d="M56.5842766 5.21462069L56.5826214 10.7084138C56.5823456 11.9271724 56.6504835 12.4686897 57.2320007 12.4684138 57.8303456 12.4681379 57.8990352 11.945931 57.8995869 10.7078621L57.901518 5.21434483 61.2529663 5.21296552 61.2507594 11.2289655C61.2502076 14.2466207 59.693518 15.64 57.2311732 15.6408281 54.6317249 15.641931 53.2300697 14.0173793 53.2308969 11.232L53.2328283 5.216 56.5842766 5.21462069M65.1713111 7.10565517L65.205518 7.14427586C65.5310352 6.04165517 66.4033111 4.99668966 67.5147594 4.99641352 69.4471732 4.99558621 69.925518 6.85268966 69.9246915 8.6902069L69.9227594 15.4215172 66.5707594 15.4228966 66.5724145 10.2584828C66.5726904 9.38758621 66.5216559 8.76882759 65.957518 8.76882759 65.3073111 8.76910345 65.2562766 9.46565517 65.2557249 10.5103448L65.2540697 15.4234483 61.9026214 15.4248276 61.9056559 5.21131034 65.2576559 5.2102069 65.1713111 7.10565517M75.3310357 10.2342069C75.3313111 9.22813793 75.2121387 8.14455172 74.5966904 8.14482753 73.895449 8.14537931 73.7241387 9.13213793 73.7238613 10.2537931 73.7233111 11.2984828 73.8769663 12.3815172 74.5266214 12.3815172 75.2620697 12.3812414 75.3307594 11.3169655 75.3310357 10.2342069zM75.2270352 14.0835862L75.1931042 14.0446897C74.7481387 14.9732414 74.1837249 15.6311724 73.1404145 15.6317241 70.7806904 15.6322759 70.2686904 12.4601379 70.269517 10.409931 70.2703456 8.30151724 70.7158628 4.99365517 73.1440007 4.99282759 74.1873111 4.99227586 74.8537938 5.64965517 75.2468973 6.67475862L75.2811042 6.63613793C75.2642766 6.17186207 75.2300697 5.70731034 75.2300697 5.24358621L75.2320007.00110344828 78.5837249-1.77635684e-14 78.5784835 15.4171034 75.1583456 15.4182069 75.2270352 14.0835862 75.2270352 14.0835862z" />
                    </g>
                  </svg>
                )}
              </div>
            </LinkWrapper>
            <div className={styles.rightWrapper}>
              <CartContextConsumer>
                {(cartContext) => (
                  <div key={cartContext.cartUpdatedKey} className={styles.cartIconWrapper}>
                    {cartContext.totalItems > 0 && (
                      <>
                        <GhostButton
                          className={["waves-effect", styles.menuBtn, this.context.showCart ? styles.show : ""].join(
                            " ",
                          )}
                          onClick={() => {
                            this.toggleCart();
                            EventAggregator.publish("trackEvent", {
                              category: "Navigation",
                              action: "Opened",
                              label: "Cart",
                            });
                          }}
                          iconCenter="icon-cart"
                        />
                        <span
                          key={cartContext.cartUpdatedKey + "top"}
                          className={[
                            styles.itemsInCart,
                            cartContext.isInitialCartFetch === null || cartContext.isInitialCartFetch
                              ? ""
                              : styles.addItem,
                          ].join(" ")}
                        >
                          {this.setTotalItemsInCart(cartContext)}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </CartContextConsumer>
              <GhostButton
                className={[styles.menuBtn, styles.btnIconLeft, "waves-effect"].join(" ")}
                onClick={() => {
                  this.toggleMore();
                  EventAggregator.publish("trackEvent", {
                    category: "Navigation",
                    action: "Opened",
                    label: "Menu",
                  });
                }}
                label={this.menuIconTitle ? this.menuIconTitle : undefined}
                labelClass={styles.btnLabel}
                iconLeft="icon-menu"
              />
            </div>
          </div>
        </div>

        <AnimatePresence>
          {this.context.showMenu && (
            <motion.div
              initial={{ right: "-100vw" }}
              animate={{ right: 0 }}
              exit={{ display: "none" }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              onTransitionEnd={this.onTransitionEndCollapsedElement}
              className={[styles.collapsedMoreWrapper, this.context.showMenu ? styles.show : ""].join(" ")}
            >
              <GhostButton
                className={[styles.closeBtn, styles.menuBtn, "waves-effect"].join(" ")}
                onClick={() => {
                  this.toggleMore();
                  EventAggregator.publish("trackEvent", {
                    category: "Navigation",
                    action: "Closed",
                    label: "Menu",
                  });
                }}
                iconCenter="icon-close"
              />
              {(this.props.siteConfig.footerLogo || this.props.navigation.logoForOpenMenu) && (
                <LinkWrapper
                  onPreNavigate={this.onClickLogo.bind(this)}
                  url={this.props.isStartPage ? undefined : this.logoTransparentUrl}
                >
                  <GatsbyImage
                    className={styles.moreMenuLogo}
                    image={
                      this.props.navigation.logoForOpenMenu?.gatsbyImageData ||
                      this.props.siteConfig.footerLogo?.gatsbyImageData
                    }
                    alt="Gröna Lund"
                  />
                </LinkWrapper>
              )}
              <div>
                {this.loginEnabled && (
                  <div className={[styles.menuSection, styles.loginSection].join(" ")}>
                    <LoginLink />
                  </div>
                )}
                {this.linkGroups &&
                  this.linkGroups.map((linkGroup) => {
                    return (
                      <div key={linkGroup.id} className={styles.menuSection}>
                        {linkGroup.headline && (
                          <h4 className={[styles.groupHeadline, "body-s"].join(" ")}> {linkGroup.headline} </h4>
                        )}
                        <ul className={styles.moreLinksList}>
                          {linkGroup.listObjects instanceof Array &&
                            linkGroup.listObjects.map((listItem) => {
                              return (
                                <Link
                                  key={listItem.id}
                                  className={styles.moreLinksItemUrl}
                                  activeClassName={styles.active}
                                  to={listItem.internalLink && listItem.internalLink.slug}
                                  onClick={() => {
                                    this.closeMenu();
                                    EventAggregator.publish("trackEvent", {
                                      category: "Navigation",
                                      action: "Clicked link",
                                      label: listItem.title && listItem.title,
                                    });
                                  }}
                                >
                                  <li
                                    className={[styles.moreLinksItem, linkGroup.secondary ? "body-s" : "h5"].join(" ")}
                                  >
                                    {listItem.title && listItem.title}
                                  </li>
                                </Link>
                              );
                            })}
                        </ul>
                      </div>
                    );
                  })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {this.context.showCart && (
            <motion.div
              initial={{ right: "-100vw" }}
              animate={{ right: 0 }}
              exit={{ display: "none" }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              onTransitionEnd={this.onTransitionEndCollapsedElement}
              className={[styles.cartWrapper, this.context.showCart ? styles.show : ""].join(" ")}
            >
              <div className={styles.cartInnerWrapper}>
                <OutlineButton
                  className={[styles.closeBtn, "waves-effect"].join(" ")}
                  onClick={() => {
                    this.toggleCart();
                    EventAggregator.publish("trackEvent", {
                      category: "Navigation",
                      action: "Closed",
                      label: "Cart",
                    });
                  }}
                  iconCenter="icon-close"
                />
                <Cart />
              </div>
              <div
                aria-label="Close overlay"
                className={styles.overlay}
                tabIndex={0}
                role="button"
                onKeyDown={() => this.toggleCart()}
                onClick={() => this.toggleCart()}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {this.context.showSearch && (
            <motion.div
              initial={{ right: "-100vw" }}
              animate={{ right: 0 }}
              exit={{ display: "none" }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              onTransitionEnd={this.onTransitionEndCollapsedElement}
              className={[styles.collapsedSearchWrapper, this.context.showSearch ? styles.show : ""].join(" ")}
            >
              <GhostButton
                className={[styles.closeBtn, styles.menuBtn, "waves-effect"].join(" ")}
                onClick={() => this.toggleSearch()}
                iconCenter="icon-close"
              />
              {(this.props.siteConfig.footerLogo || this.props.navigation.logoForOpenMenu) && (
                <LinkWrapper
                  onPreNavigate={this.onClickLogo.bind(this)}
                  url={this.props.isStartPage ? undefined : this.logoTransparentUrl}
                >
                  <GatsbyImage
                    className={styles.moreMenuLogo}
                    image={
                      this.props.navigation.logoForOpenMenu?.gatsbyImageData ||
                      this.props.siteConfig.footerLogo?.gatsbyImageData
                    }
                    alt="Gröna Lund"
                  />
                </LinkWrapper>
              )}
              <div className="block--max-width-narrow">
                <LunrSearch
                  searchResultHeading={this.searchResultHeader}
                  searchPlaceHolder={this.searchPlaceholderLabel}
                  noSearchResultText={this.searchNoResultText}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    );
  }
}

MainNavigation.contextType = GlobalContext;

export const fragmentMainNavigation = graphql`
  fragment MainNavigation on ContentfulMainNavigation {
    __typename
    id
    menuIconTitle
    linkGroups {
      id
      headline
      secondary
      listObjects {
        __typename
        ... on ContentfulLink {
          id
          title
          internalLink {
            slug
          }
          externalUrl
        }
      }
    }
    logoTransparent {
      internalLink {
        slug
      }
      image {
        file {
          url
        }
      }
    }
    logoFilled {
      internalLink {
        slug
      }
      image {
        file {
          url
        }
      }
    }
    logoForOpenMenu {
      file {
        url
      }
      gatsbyImageData(width: 90, layout: FIXED, placeholder: NONE, quality: 100)
    }
    extraWideLogo
    searchPlaceholderLabel
    searchResultHeader
    searchNoResultText
    languageSelectionList {
      languages {
        url
        title
        locale
      }
    }
  }
`;
